import type * as React from "react";
import { SourceDrawer, type SourceActionsConfig } from "@ando/ui";
import { useActiveContextScope } from "@/features/active-context";
import { useSourcesStore } from "./state/sourcesStore";
import { useWorkspaceStore } from "@/features/workspace-router/state/workspaceStore";

/**
 * SourceDrawerSurface — single mount point for the citation-pin-point
 * drawer (the only remaining drawer mode in v2 after the May 2026
 * Opzione B simplification).
 *
 * Historical: this surface used to host 4 modes (source/inbox/artifact/
 * calendar). The user feedback (May 2026) was that drawers from the
 * right are jarring for auto-open; everything else moved to inline
 * viewers in the workspace hubs (``documents.tsx`` /
 * ``conversations.tsx`` / ``calendar.tsx``) and the inline
 * ``<SourceRail>`` provides cross-source navigation. The drawer survives
 * ONLY for the future ``[n]`` citation pin-point use-case — when the BE
 * starts emitting inline ``[n]`` markers we'll re-wire the chip click via
 * ``useCitationClick`` and the user will see a precise anchor drawer for
 * a single quote (different UX from "open the whole document inline").
 *
 * Visibility: only renders when the active source's home tab matches the
 * active workspace tab — per the original §A.11 spec "cambio tab manuale
 * non lo chiude". The store keeps the drawer alive across tab switches;
 * we just hide the chrome.
 */
export interface SourceDrawerSurfaceProps {
  /** Per-viewer action handlers — bubbled to ``<SourceDrawer>``. */
  actions?: SourceActionsConfig;
}

export const SourceDrawerSurface: React.FC<SourceDrawerSurfaceProps> = ({ actions }) => {
  const activeId = useSourcesStore((s) => s.activeId);
  const drawers = useSourcesStore((s) => s.openDrawers);
  const drawerTabs = useSourcesStore((s) => s.drawerTabs);
  const closeDrawer = useSourcesStore((s) => s.closeDrawer);
  const currentTab = useWorkspaceStore((s) => s.currentTab);

  const meta = activeId ? (drawers.get(activeId) ?? null) : null;
  const ownerTab = activeId ? drawerTabs.get(activeId) : undefined;
  const visible = Boolean(meta && ownerTab === currentTab);

  // Active context — the pin-point citation drawer is the canonical
  // "user has a source open" signal. Surface only when the drawer is
  // actually visible (a stale meta on a hidden tab shouldn't ground
  // the agent in something the user isn't looking at).
  useActiveContextScope(
    visible && meta
      ? {
          kind: "source_citation",
          id: meta.id,
          title: meta.title,
          summary: typeof meta.meta === "string" ? meta.meta : undefined,
          meta: {
            source_kind: meta.kind,
            domain_type: meta.domainType,
          },
        }
      : null,
  );

  return (
    <SourceDrawer
      open={visible}
      onOpenChange={(open) => {
        if (!open && activeId) closeDrawer(activeId);
      }}
      meta={meta}
      actions={actions}
    />
  );
};
SourceDrawerSurface.displayName = "SourceDrawerSurface";
